import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import httpService from '../../services/httpService';
import InteractionsTab from './ClubStatisticsTabs/InteractionsTab';
import EpisodesTab from './ClubStatisticsTabs/EpisodesTab';
import ListenersTab from './ClubStatisticsTabs/ListenersTab';
import SubscribersTab from './ClubStatisticsTabs/SubscribersTab';
import { InfoTip } from './ClubStatisticsTabs/shared';

const TILE_META = {
    episodes_published: {
        tip: "The total number of episodes added and published in this club. Each club supports up to 6 episodes.",
    },
    episodes_with_interaction: {
        tip: "Episodes where at least one listener recorded a practice, left a practice comment, or posted in the discussion after release. An episode with zero interactions was released but sparked no follow-up activity.",
    },
    total_interactions: {
        tip: "The sum of every practice recorded, practice comment posted, discussion comment made, and reaction given — across all episodes in this club.",
    },
    avg_interactions_per_episode: {
        tip: "Total interactions ÷ episodes published. A normalised signal of engagement depth — useful for comparing clubs of different lengths.",
    },
    practice_completion_rate: {
        tip: "The percentage of practices marked as completed out of all practices created across this club. A high rate means listeners are following through on their commitments.",
    },
    active_subscribers: {
        tip: "Subscribers who recorded a practice or posted a comment in the last 30 days. This is your current active audience — the listeners who are still actively learning.",
    },
};

const TABS = [
    { key: 'interactions', label: 'Interactions',  Component: InteractionsTab },
    { key: 'episodes',     label: 'Episodes',       Component: EpisodesTab },
    { key: 'listeners',    label: 'Listeners',      Component: ListenersTab },
    { key: 'subscribers',  label: 'Subscribers',    Component: SubscribersTab },
];

const ClubStatistics = ({ clubGuid }) => {
    const [tiles, setTiles] = useState([]);
    const [tilesLoading, setTilesLoading] = useState(true);
    const [tilesError, setTilesError] = useState(null);
    const [activeTab, setActiveTab] = useState('interactions');

    useEffect(() => {
        if (!clubGuid) return;
        setTilesLoading(true);
        setTilesError(null);
        httpService.get(`/dash/podcast-club/${clubGuid}/statistics`)
            .then((res) => setTiles(res.data.tiles || []))
            .catch((err) => setTilesError(err.response?.data?.message || 'Error loading statistics'))
            .finally(() => setTilesLoading(false));
    }, [clubGuid]);

    const handleTabSelect = (tab) => setActiveTab(tab);

    return (
        <div className="py-3 component-ClubStatistics">

            {/* Overview tiles */}
            {tilesLoading ? (
                <Row className="g-3 mb-4">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <Col xs={6} lg={2} key={i}>
                            <div className="card h-100 border-0 stat-card p-3 text-center rounded-3">
                                <div className="display-6 fw-bold text-muted">—</div>
                                <div className="text-muted small mt-1"><Spinner animation="border" size="sm" /></div>
                            </div>
                        </Col>
                    ))}
                </Row>
            ) : tilesError ? (
                <Alert variant="danger" className="mb-4">{tilesError}</Alert>
            ) : (
                <Row className="g-3 mb-4">
                    {tiles.map((tile) => (
                        <Col xs={6} lg={2} key={tile.key}>
                            <div className="card h-100 border-0 stat-card p-3 text-center rounded-3">
                                <div className="display-6 fw-bold">{tile.value}</div>
                                <div className="text-muted small mt-1">
                                    {tile.title}
                                    {TILE_META[tile.key]?.tip && <InfoTip text={TILE_META[tile.key].tip} />}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Sub-tabs */}
            <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabSelect} className="mb-4">
                {TABS.map(({ key, label }) => (
                    <Nav.Item key={key}>
                        <Nav.Link eventKey={key}>{label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {/* Tab panels — all mount immediately, shown/hidden by CSS */}
            {TABS.map(({ key, Component }) => (
                <div key={key} style={{ display: activeTab === key ? 'block' : 'none' }}>
                    <Component clubGuid={clubGuid} />
                </div>
            ))}

        </div>
    );
};

export default ClubStatistics;
